// ============================================================
// SALT — Leaderboard Preview Component
// Top 8 teams with sortable PTS / W / L columns
// ============================================================

import React, { useState, useMemo } from 'react';
import type { LeaderboardEntry } from '../../types/dashboard.types';
import { getRankClass, DefaultTeamIcon } from './DashboardHelpers';

// ── Props ──────────────────────────────────────────────────

interface LeaderboardPreviewProps {
  entries: LeaderboardEntry[];
  loading: boolean;
  onViewFull: () => void;
  onTeamClick: (teamId: number) => void;
}

// ── Component ──────────────────────────────────────────────

const LeaderboardPreview: React.FC<LeaderboardPreviewProps> = ({
  entries,
  loading,
  onViewFull,
  onTeamClick,
}) => {
  const [sortKey, setSortKey] = useState<'points' | 'wins' | 'losses'>('points');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    if (!entries.length) return entries;
    return [...entries].sort((a, b) => {
      const mul = sortDir === 'desc' ? -1 : 1;
      return (a[sortKey] - b[sortKey]) * mul;
    });
  }, [entries, sortKey, sortDir]);

  const handleSort = (key: 'points' | 'wins' | 'losses') => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <section className="sl-card sl-leaderboard">
      <div className="sl-card-header sl-leaderboard-header-bar">
        <h2 className="sl-section-title">Leaderboard</h2>
      </div>

      {loading ? (
        <div className="sl-leaderboard-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="sl-skeleton-row sl-skeleton-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className="sl-leaderboard-empty">
          No teams on the leaderboard yet. Compete in a match to get ranked!
        </div>
      ) : (
        <div className="sl-leaderboard-table">
          {/* Column headers */}
          <div className="sl-leaderboard-table-header">
            <span>#</span>
            <span className="sl-leaderboard-table-header-team">Team</span>
            <SortButton
              label="PTS"
              sortKey="points"
              activeSortKey={sortKey}
              sortDir={sortDir}
              onClick={handleSort}
            />
            <SortButton
              label="W"
              sortKey="wins"
              activeSortKey={sortKey}
              sortDir={sortDir}
              onClick={handleSort}
            />
            <SortButton
              label="L"
              sortKey="losses"
              activeSortKey={sortKey}
              sortDir={sortDir}
              onClick={handleSort}
            />
          </div>

          {/* Rows */}
          {sorted.map((entry) => (
            <button
              key={entry.teamId}
              className="sl-leaderboard-row"
              onClick={() => onTeamClick(entry.teamId)}
            >
              <span className={`sl-leaderboard-rank ${getRankClass(entry.rank)}`}>
                {entry.rank ?? '—'}
              </span>
              <div className="sl-leaderboard-team">
                <div className="sl-leaderboard-team-main">
                  <div className="sl-leaderboard-team-logo-wrap">
                    {entry.logoUrl ? (
                      <img
                        src={entry.logoUrl}
                        alt=""
                        className="sl-leaderboard-team-logo"
                      />
                    ) : (
                      <DefaultTeamIcon />
                    )}
                  </div>
                  <div className="sl-leaderboard-team-text">
                    <span className="sl-leaderboard-team-name">
                      {entry.teamName}
                    </span>
                  </div>
                </div>
              </div>
              <span className="sl-leaderboard-stat sl-leaderboard-stat--points">
                {entry.points}
              </span>
              <span className="sl-leaderboard-stat">{entry.wins}</span>
              <span className="sl-leaderboard-stat">{entry.losses}</span>
            </button>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="sl-leaderboard-footer">
        <button className="sl-link-button sl-leaderboard-link" onClick={onViewFull}>
          View full leaderboard
        </button>
      </div>
    </section>
  );
};

export default LeaderboardPreview;

// ── Sort Button Sub-component ──────────────────────────────

interface SortButtonProps {
  label: string;
  sortKey: 'points' | 'wins' | 'losses';
  activeSortKey: string;
  sortDir: 'asc' | 'desc';
  onClick: (key: 'points' | 'wins' | 'losses') => void;
}

const SortButton: React.FC<SortButtonProps> = ({
  label,
  sortKey,
  activeSortKey,
  sortDir,
  onClick,
}) => {
  const isActive = activeSortKey === sortKey;
  return (
    <button
      className={`sl-leaderboard-table-header-stat ${
        isActive ? 'sl-leaderboard-table-header-stat--active' : ''
      }`}
      onClick={() => onClick(sortKey)}
    >
      {label}
      {isActive && (
        <span className="sl-leaderboard-sort-indicator">
          {sortDir === 'desc' ? '▼' : '▲'}
        </span>
      )}
    </button>
  );
};